import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { FyMonthBucket } from "../data/leaveSeed";
import { roundHours } from "../data/leaveSeed";

type Props = {
  buckets: FyMonthBucket[];
  asOf: string;
  fyLabel: string;
};

export function LeaveBalanceChart({ buckets, asOf, fyLabel }: Props) {
  const data = buckets.map((b) => ({
    label: b.label,
    usedActual: b.isActual ? b.used : null,
    usedProjected: b.isProjected ? b.used : null,
    balanceActual: b.isActual ? b.balance : null,
    balanceProjected: b.isProjected ? b.balance : null,
    accrued: b.accrued,
    tip: `${b.start} → ${b.end}`,
  }));

  const maxUsed = Math.max(...buckets.map((b) => b.used), 8);
  const maxBal = Math.max(...buckets.map((b) => b.balance), 1);
  const minBal = Math.min(...buckets.map((b) => b.balance), 0);

  return (
    <div className="w-full">
      <p className="text-xs text-slate mb-2">
        Company FY {fyLabel} · as of {asOf} · left axis: hours used · right axis: ending balance
      </p>
      <div className="h-72 w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
            <YAxis
              yAxisId="used"
              orientation="left"
              tick={{ fontSize: 11, fill: "#64748b" }}
              domain={[0, Math.ceil(maxUsed * 1.15)]}
              label={{
                value: "Hours used",
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 11, fill: "#64748b" },
              }}
            />
            <YAxis
              yAxisId="balance"
              orientation="right"
              tick={{ fontSize: 11, fill: "#64748b" }}
              domain={[Math.min(0, Math.floor(minBal * 1.05)), Math.ceil(maxBal * 1.1)]}
              label={{
                value: "Ending balance (hrs)",
                angle: 90,
                position: "insideRight",
                style: { fontSize: 11, fill: "#64748b" },
              }}
            />
            <Tooltip
              contentStyle={{ fontSize: 12 }}
              formatter={(value, name) => {
                if (value == null || Number.isNaN(Number(value))) return ["—", String(name)];
                return [`${roundHours(Number(value))} hrs`, String(name)];
              }}
              labelFormatter={(label, payload) => {
                const tip = payload?.[0]?.payload?.tip;
                return tip ? `${label} (${tip})` : String(label);
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              yAxisId="used"
              dataKey="usedActual"
              name="Used (actual)"
              fill="#0d9488"
              maxBarSize={22}
            />
            <Bar
              yAxisId="used"
              dataKey="usedProjected"
              name="Used (projected)"
              fill="#94a3b8"
              maxBarSize={22}
            />
            <Line
              yAxisId="balance"
              type="monotone"
              dataKey="balanceActual"
              name="Balance (actual)"
              stroke="#0f172a"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls={false}
            />
            <Line
              yAxisId="balance"
              type="monotone"
              dataKey="balanceProjected"
              name="Balance (projected)"
              stroke="#0f172a"
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={{ r: 3 }}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
